/**
 * Driver Service (Frontend)
 * API calls för förare, schema och fordonstilldelning
 */

import api from '../api/client';

export type DriverScheduleMode = 'private_manual' | 'work_hours' | 'company_car';

export interface ScheduleEntry {
  day_of_week: number; // 0=Monday, 6=Sunday
  start_time: string;  // HH:MM
  end_time: string;    // HH:MM
}

export interface ScheduleRow extends ScheduleEntry {
  id: number;
  driver_id: number;
  created_at: string;
  updated_at: string;
}

export interface DriverScheduleData {
  mode: DriverScheduleMode;
  schedule: ScheduleRow[];
}

export interface SetDriverSchedulePayload {
  mode?: DriverScheduleMode;
  schedule?: ScheduleEntry[];
}

export interface AssignmentRow {
  id: number;
  driver_id: number;
  vehicle_id: number;
  start_date: string;
  start_trip_id: string | null;
  end_date: string | null;
  end_trip_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  driver_name?: string;
  vehicle_registration?: string;
}

export interface TripOnDate {
  id: number;
  trip_id: string;
  start_time: string;
  end_time: string;
  distance_km: number;
  duration_seconds: number;
  start_address: string | null;
  end_address: string | null;
  driver_id: string | null;
  status: string;
  purpose: string | null;
}

export const driverService = {
  getDriverSchedule: async (driverId: number): Promise<DriverScheduleData> => {
    const response = await api.get(`/api/drivers/${driverId}/schedule`);
    const data = response.data.data;
    if (Array.isArray(data)) {
      return { mode: data.length > 0 ? 'work_hours' : 'private_manual', schedule: data };
    }
    return data as DriverScheduleData;
  },

  setDriverSchedule: async (
    driverId: number,
    payload: SetDriverSchedulePayload
  ): Promise<DriverScheduleData> => {
    const response = await api.put(`/api/drivers/${driverId}/schedule`, payload);
    return response.data.data as DriverScheduleData;
  },

  /** @deprecated Använd getDriverSchedule */
  getSchedule: async (driverId: number): Promise<ScheduleRow[]> => {
    const { schedule } = await driverService.getDriverSchedule(driverId);
    return schedule;
  },

  /** @deprecated Använd setDriverSchedule */
  setSchedule: async (driverId: number, schedule: ScheduleEntry[]): Promise<ScheduleRow[]> => {
    const result = await driverService.setDriverSchedule(driverId, {
      mode: 'work_hours',
      schedule,
    });
    return result.schedule;
  },

  assignDriverToVehicle: async (data: {
    driverId: number;
    vehicleId: number;
    startDate: string;
    startTripId?: string;
  }): Promise<AssignmentRow> => {
    const response = await api.post('/api/drivers/vehicle-assignment', data);
    return response.data.data;
  },

  endAssignment: async (assignmentId: number, endDate: string, endTripId?: string): Promise<void> => {
    await api.put(`/api/drivers/vehicle-assignment/${assignmentId}/end`, { endDate, endTripId });
  },

  getAssignments: async (driverId: number): Promise<AssignmentRow[]> => {
    const response = await api.get(`/api/drivers/${driverId}/assignments`);
    return response.data.data;
  },

  getTripsOnDate: async (vehicleId: number, date: string): Promise<TripOnDate[]> => {
    const response = await api.get(`/api/vehicles/${vehicleId}/trips-on-date`, { params: { date } });
    return response.data.data;
  },

  getCurrentDriver: async (vehicleId: number): Promise<AssignmentRow | null> => {
    const response = await api.get(`/api/vehicles/${vehicleId}/current-driver`);
    return response.data.data;
  },
};
